/**
 * Pure, testable helpers for the "Captured items on this wheel" surface
 * (ISP-4068 / WL-021b). No React, no side effects.
 *
 * The surface reads back the captures a user attached to a given wheel (the
 * backend `?attached_wheel=<id>` filter) and renders them with the deck's
 * loading / empty / error states. The state selection is pure so it can be unit
 * tested without a DOM.
 */

export const WHEEL_CAPTURE_STATE = {
  LOADING: "loading",
  ERROR: "error",
  EMPTY: "empty",
  POPULATED: "populated",
};

/**
 * Select which of the four render states the surface should show.
 *
 * Precedence mirrors react-query semantics: an initial LOADING (no data yet)
 * wins; then a hard ERROR with nothing cached; then EMPTY vs POPULATED by the
 * item count. A background refetch error while items are already cached keeps
 * showing the items (POPULATED) rather than blanking the list — the caller can
 * surface a non-blocking retry affordance separately.
 *
 * @param {{isLoading?:boolean, isError?:boolean, itemCount?:number}} flags
 * @returns {string} one of WHEEL_CAPTURE_STATE
 */
export const wheelCaptureState = ({ isLoading = false, isError = false, itemCount = 0 } = {}) => {
  if (isLoading && itemCount === 0) return WHEEL_CAPTURE_STATE.LOADING;
  if (isError && itemCount === 0) return WHEEL_CAPTURE_STATE.ERROR;
  if (itemCount === 0) return WHEEL_CAPTURE_STATE.EMPTY;
  return WHEEL_CAPTURE_STATE.POPULATED;
};

/**
 * Total attached-capture count across DRF pages. `results` is the page array;
 * `count` (when present) is DRF's total. Prefer the flattened length so it stays
 * correct as more pages load in. Pure.
 */
export const wheelCaptureCount = (items = []) => (Array.isArray(items) ? items.length : 0);

/**
 * A short accessible summary line for the drawer header, e.g. "2 items" / "No
 * items yet". Pure — drives the header count + the empty state copy.
 */
export const wheelCaptureSummary = (itemCount = 0) => {
  if (!itemCount) return "No items yet";
  return `${itemCount} item${itemCount === 1 ? "" : "s"}`;
};
